import { AppState, EventPageState, SchemaPageState, Source } from "duck/types";
import qs from "qs";
import { matchRoutes } from "react-router";

import { getSortFilter } from "shared/api/utils";
import { EventTypeEnum } from "shared/types";

import {
  VEHICLES_PAGE_KEY as CLAIM_ANALYTICS_VEHICLES_PAGE_KEY,
  CLAIMS_PAGE_KEY,
  CLAIMS_TABLE_DEFAULT_SORT,
  CLAIMS_TABLE_PAGE_KEY,
} from "pages/ClaimAnalytics/constants";
import {
  CLAIM_ANALYTICS_TOP_CONTRIBUTORS_GROUP_BY_OPTIONS_KEY,
  DEFAULT_GROUP_BY_ATTRIBUTE as DEFAULT_CLAIM_ANALYTICS_TOP_CONTRIBUTORS_GROUP_BY_ATTRIBUTE,
} from "pages/ClaimAnalytics/tabPages/TopContributors/constants";
import {
  GROUP_BY_ATTRIBUTE_KEY,
  TOP_CONTRIBUTORS_TAB_KEY,
} from "pages/constants";
import {
  DASHBOARD_EVENT_FILTERS_LOCALSTORAGE_KEY,
  DASHBOARD_EVENT_TYPE_LOCALSTORAGE_KEY,
  DASHBOARD_VEHICLE_FILTERS_LOCALSTORAGE_KEY,
} from "pages/Dashboard/constants";
import {
  INVESTIGATOR_EVENT_FILTER,
  INVESTIGATOR_EVENT_TYPE,
  INVESTIGATOR_VEHICLE_FILTER,
} from "pages/Investigator/constants";
import { mapEventTypeToSource } from "pages/Investigator/utils";
import {
  SCHEMA_DASHBOARD_FILTERS_LOCALSTORAGE_KEY,
  SchemaDashboardDuckFilters,
} from "pages/SchemaDashboard/constants";
import {
  getSchemaDashboardViewPageKey,
  parseSchemaDashboardTabKey,
  SchemaDashboardTabKey,
} from "pages/SchemaDashboard/utils";
import { getSchemaEventAnalyticsPageKey } from "pages/SchemaEventAnalytics/constants";
import {
  SCHEMA_INVESTIGATOR_ASSET_FILTER_KEY,
  SCHEMA_INVESTIGATOR_EVENT_FILTER_KEY,
  SCHEMA_INVESTIGATOR_INVESTIGATION_ID_KEY,
  SCHEMA_INVESTIGATOR_SMART_GROUP_ID_KEY,
} from "pages/SchemaInvestigator/constants";
import { loadInvestigatorFiltersFromLocalStorage } from "pages/SchemaInvestigator/filtersStorage";
import { getSchemaRecordPageKey } from "pages/SchemaRecords/constants";
import {
  VEHICLES_PAGE_KEY as SIGNAL_EVENTS_ANALYTICS_VEHICLES_PAGE_KEY,
  SIGNAL_EVENTS_PAGE_KEY,
  SIGNAL_EVENTS_TABLE_PAGE_KEY,
} from "pages/SignalEventsAnalytics/constants";
import {
  DEFAULT_GROUP_BY_ATTRIBUTE as DEFAULT_SIGNAL_EVENTS_TOP_CONTRIBUTORS_GROUP_BY_ATTRIBUTE,
  SIGNAL_EVENTS_TOP_CONTRIBUTORS_GROUP_BY_OPTIONS_KEY,
} from "pages/SignalEventsAnalytics/tabPages/TopContributors/constants";
import { PagesConfigModel } from "pages/SuperAdmin/PagesConfig/types";
import { getTablePageKey } from "pages/utils";

import { FilterRowState } from "features/ui/Filters/FilterBuilder/types";
import {
  createFilterGroupStateFromFilterRowStateArray,
  filterBuilderQueryToFilterBuilderState,
  getFiltersQuery,
  mergeFilterQueryStrings,
} from "features/ui/Filters/FilterBuilder/utils";
import {
  FilterSortState,
  PageChartSettingsState,
} from "features/ui/Filters/types";
import {
  getPageKeyWithVersion,
  getStateFromLocalStorage,
} from "features/ui/Filters/utils";

import { RouteKey, routes } from "services/routes";

import { DUCK_VISIBILITY_KEY } from "./constants";

const getSelectedGroupByAttribute = (
  chartSettings: PageChartSettingsState | undefined,
  optionsKey: string,
  defaultGroupByAttribute: string
): string => {
  if (
    chartSettings &&
    chartSettings[TOP_CONTRIBUTORS_TAB_KEY] &&
    chartSettings[TOP_CONTRIBUTORS_TAB_KEY][optionsKey]
  ) {
    const foundGroupBySetting = chartSettings[TOP_CONTRIBUTORS_TAB_KEY][
      optionsKey
    ].find((option) => option.id === GROUP_BY_ATTRIBUTE_KEY);
    if (foundGroupBySetting) {
      return String(foundGroupBySetting.optionId);
    }
  }

  return defaultGroupByAttribute;
};

export const getInitialDuckVisibility = (): boolean => {
  if (sessionStorage) {
    const visibilityFromStorage = sessionStorage.getItem(DUCK_VISIBILITY_KEY);
    if (visibilityFromStorage) {
      return true;
    }
  }

  return false;
};

export const persistVisibility = (open: boolean) => {
  if (sessionStorage) {
    if (open) {
      sessionStorage.setItem(DUCK_VISIBILITY_KEY, "true");
    } else {
      sessionStorage.removeItem(DUCK_VISIBILITY_KEY);
    }
  }
};

/**
 * Quick filters sometimes include a degenerate rows with no values.
 * This function discards those if they exist.
 * If those degenerate rows are left in there, the conversion to a query string
 * filter results in an empty string.
 */
const removeQuickFilterRowsWithNoValues = (
  quickFilters: FilterRowState[] | undefined
): FilterRowState[] | undefined =>
  quickFilters?.filter(({ values }) => values?.length);

/**
 * Combine a filter-sort state's advanced filters with its non-empty quick
 * filters (plus any additional pre-built query strings, e.g. table-column
 * filters) into a single filter query string.
 */
const getMergedFilterQuery = (
  state: FilterSortState,
  ...additionalQueries: string[]
): string =>
  mergeFilterQueryStrings(
    getFiltersQuery(state.filters),
    getFiltersQuery(
      createFilterGroupStateFromFilterRowStateArray(
        removeQuickFilterRowsWithNoValues(state.quickFilters)
      )
    ),
    ...additionalQueries
  );

const getDashboardPageState = ({}): EventPageState => {
  const dashboardEventsFilters =
    localStorage.getItem(DASHBOARD_EVENT_FILTERS_LOCALSTORAGE_KEY) || "";
  const dashboardVehicleFilters =
    localStorage.getItem(DASHBOARD_VEHICLE_FILTERS_LOCALSTORAGE_KEY) || "";

  const investigatorEventType =
    localStorage.getItem(DASHBOARD_EVENT_TYPE_LOCALSTORAGE_KEY) || "";

  return {
    events_filter: dashboardEventsFilters,
    vehicles_filter: dashboardVehicleFilters,
    event_type: mapEventTypeToSource(investigatorEventType as EventTypeEnum),
  };
};

const getInvestigatorPageState = ({}): EventPageState => {
  const investigatorEventsFilter =
    localStorage.getItem(INVESTIGATOR_EVENT_FILTER) || "";
  const investigatorVehiclesFilter =
    localStorage.getItem(INVESTIGATOR_VEHICLE_FILTER) || "";

  const investigatorEventType =
    localStorage.getItem(INVESTIGATOR_EVENT_TYPE) || "";

  return {
    events_filter: investigatorEventsFilter,
    vehicles_filter: investigatorVehiclesFilter,
    event_type: mapEventTypeToSource(investigatorEventType as EventTypeEnum),
  };
};

const getClaimAnalyticsPageState = ({
  claimAnalytics,
}: PagesConfigModel): EventPageState => {
  const claimsPageKeyWithVersion = getPageKeyWithVersion(CLAIMS_PAGE_KEY);
  const defaultClaimFilters = filterBuilderQueryToFilterBuilderState(
    claimAnalytics?.defaultFilters
  );
  const claimFilterSortState = getStateFromLocalStorage(
    claimsPageKeyWithVersion,
    defaultClaimFilters
  );

  const vehiclesPageKeyWithVersion = getPageKeyWithVersion(
    CLAIM_ANALYTICS_VEHICLES_PAGE_KEY
  );
  const defaultVehicleFilters = filterBuilderQueryToFilterBuilderState(
    claimAnalytics?.defaultVehicleFilters
  );
  const vehiclesFilterSortState = getStateFromLocalStorage(
    vehiclesPageKeyWithVersion,
    defaultVehicleFilters
  );

  const claimsTablePageKeyWithVersion = getPageKeyWithVersion(
    CLAIMS_TABLE_PAGE_KEY
  );
  const claimTableFilterSortState = getStateFromLocalStorage(
    claimsTablePageKeyWithVersion
  );

  return {
    event_type: Source.CLAIM,
    events_filter: mergeFilterQueryStrings(
      getFiltersQuery(claimFilterSortState.filters),
      getFiltersQuery(
        createFilterGroupStateFromFilterRowStateArray(
          removeQuickFilterRowsWithNoValues(claimFilterSortState.quickFilters)
        )
      ),
      getFiltersQuery(claimTableFilterSortState.filters)
    ),
    vehicles_filter: mergeFilterQueryStrings(
      getFiltersQuery(vehiclesFilterSortState.filters),
      getFiltersQuery(
        createFilterGroupStateFromFilterRowStateArray(
          removeQuickFilterRowsWithNoValues(
            vehiclesFilterSortState.quickFilters
          )
        )
      )
    ),
    events_table_sort:
      getSortFilter(claimTableFilterSortState.sort) ??
      getSortFilter(CLAIMS_TABLE_DEFAULT_SORT) ??
      "",
    selected_group_by_attribute: getSelectedGroupByAttribute(
      claimFilterSortState.chartSettings,
      CLAIM_ANALYTICS_TOP_CONTRIBUTORS_GROUP_BY_OPTIONS_KEY,
      DEFAULT_CLAIM_ANALYTICS_TOP_CONTRIBUTORS_GROUP_BY_ATTRIBUTE
    ),
  };
};

const getSignalEventAnalyticsPageState = ({
  signalEventsAnalytics,
}: PagesConfigModel): EventPageState => {
  const signalEventsPageKeyWithVersion = getPageKeyWithVersion(
    SIGNAL_EVENTS_PAGE_KEY
  );
  const defaultSignalEventFilters = filterBuilderQueryToFilterBuilderState(
    signalEventsAnalytics?.defaultFilters
  );
  const signalEventFilterSortState = getStateFromLocalStorage(
    signalEventsPageKeyWithVersion,
    defaultSignalEventFilters
  );

  const signalEventsTablePageKeyWithVersion = getPageKeyWithVersion(
    SIGNAL_EVENTS_TABLE_PAGE_KEY
  );
  const signalEventTableFilterSortState = getStateFromLocalStorage(
    signalEventsTablePageKeyWithVersion
  );

  const vehiclesPageKeyWithVersion = getPageKeyWithVersion(
    SIGNAL_EVENTS_ANALYTICS_VEHICLES_PAGE_KEY
  );
  const defaultVehicleFilters = filterBuilderQueryToFilterBuilderState(
    signalEventsAnalytics?.defaultVehicleFilters
  );
  const vehiclesFilterSortState = getStateFromLocalStorage(
    vehiclesPageKeyWithVersion,
    defaultVehicleFilters
  );

  return {
    event_type: Source.SIGNAL_EVENT_OCCURRENCE,
    events_filter: mergeFilterQueryStrings(
      getFiltersQuery(signalEventFilterSortState.filters),
      getFiltersQuery(
        createFilterGroupStateFromFilterRowStateArray(
          removeQuickFilterRowsWithNoValues(
            signalEventFilterSortState.quickFilters
          )
        )
      ),
      getFiltersQuery(signalEventTableFilterSortState.filters)
    ),
    vehicles_filter: mergeFilterQueryStrings(
      getFiltersQuery(vehiclesFilterSortState.filters),
      getFiltersQuery(
        createFilterGroupStateFromFilterRowStateArray(
          removeQuickFilterRowsWithNoValues(
            vehiclesFilterSortState.quickFilters
          )
        )
      )
    ),
    selected_group_by_attribute: getSelectedGroupByAttribute(
      signalEventFilterSortState.chartSettings,
      SIGNAL_EVENTS_TOP_CONTRIBUTORS_GROUP_BY_OPTIONS_KEY,
      DEFAULT_SIGNAL_EVENTS_TOP_CONTRIBUTORS_GROUP_BY_ATTRIBUTE
    ),
    events_table_sort:
      getSortFilter(signalEventTableFilterSortState.sort) || "",
  };
};

type RouteParams = Partial<Record<string, string>>;

interface RouteMatch {
  key: RouteKey;
  params: RouteParams;
}

export const getRouteMatch = (url: string): RouteMatch | undefined => {
  const routesForMatching = Object.entries(routes).map((route) => ({
    path: route[1],
    key: route[0],
  }));

  const matches = matchRoutes(routesForMatching, url);

  if (matches && matches.length > 0) {
    return {
      key: matches[0].route.key as RouteKey,
      params: matches[0].params,
    };
  }

  return undefined;
};

const getSchemaEventAnalyticsPageState = (
  params: RouteParams
): SchemaPageState => {
  const pageKey = getSchemaEventAnalyticsPageKey(params.schemaName ?? "");
  const eventState = getStateFromLocalStorage(
    getPageKeyWithVersion(`${pageKey}-event`)
  );
  const assetState = getStateFromLocalStorage(
    getPageKeyWithVersion(`${pageKey}-asset`)
  );

  return {
    table_type: params.tableType ?? "",
    schema_name: params.schemaName,
    events_filter: getMergedFilterQuery(eventState),
    assets_filter: getMergedFilterQuery(assetState),
    events_table_sort: getSortFilter(eventState.sort) || "",
  };
};

const getSchemaRecordsPageState = (params: RouteParams): SchemaPageState => {
  const recordPageKey = getSchemaRecordPageKey(params.schemaName ?? "");
  const pageState = getStateFromLocalStorage(
    getPageKeyWithVersion(recordPageKey)
  );
  const tableState = getStateFromLocalStorage(
    getPageKeyWithVersion(getTablePageKey(recordPageKey))
  );

  return {
    table_type: params.tableType ?? "",
    schema_name: params.schemaName,
    events_filter: getMergedFilterQuery(
      pageState,
      getFiltersQuery(tableState.filters)
    ),
    events_table_sort: getSortFilter(tableState.sort) || "",
  };
};

const getSchemaRecordPageState = (params: RouteParams): SchemaPageState => ({
  table_type: params.tableType ?? "",
  schema_name: params.schemaName,
  record_id: params.id,
});

// The stored blob describes the view last rendered — only trust it when it
// matches the view the reader sees in the URL. With no view in the URL the
// mirror is the only remaining source of the dashboard context, so it is
// returned as-is.
const loadSchemaDashboardFiltersMirror = (
  tabKey: SchemaDashboardTabKey | undefined
): SchemaDashboardDuckFilters | undefined => {
  let mirror: SchemaDashboardDuckFilters;
  try {
    mirror = JSON.parse(
      localStorage.getItem(SCHEMA_DASHBOARD_FILTERS_LOCALSTORAGE_KEY) ?? ""
    );
  } catch {
    return undefined;
  }

  if (typeof mirror?.schemaName !== "string") {
    return undefined;
  }

  const matches =
    !tabKey ||
    (mirror.schemaName === tabKey.schemaName &&
      mirror.versionKey === tabKey.versionKey);

  return matches ? mirror : undefined;
};

const getSchemaDashboardPageState = (): SchemaPageState => {
  // The dashboard aggregates event-schema views only.
  const commonState: SchemaPageState = { table_type: "event" };

  const { tab } = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const tabKey =
    typeof tab === "string" ? parseSchemaDashboardTabKey(tab) : undefined;

  // A tab that isn't a view key (my-projects) means no schema view is active.
  // A missing tab falls through to the mirror of the last rendered view.
  if (typeof tab === "string" && !tabKey) {
    return commonState;
  }

  const mirror = loadSchemaDashboardFiltersMirror(tabKey);

  const schemaName = tabKey?.schemaName ?? mirror?.schemaName;
  const versionKey = tabKey?.versionKey ?? mirror?.versionKey;
  if (!schemaName || !versionKey) {
    return commonState;
  }

  const tableState = getStateFromLocalStorage(
    getPageKeyWithVersion(
      getTablePageKey(getSchemaDashboardViewPageKey(schemaName, versionKey))
    )
  );

  return {
    ...commonState,
    schema_name: schemaName,
    events_filter: getMergedFilterQuery(tableState, mirror?.eventFilter ?? ""),
    // "" states the filters are known to be empty; undefined (no mirror) that
    // they are unknown to the client.
    assets_filter: mirror ? (mirror.assetFilter ?? "") : undefined,
    events_table_sort: getSortFilter(tableState.sort) || "",
  };
};

const getSchemaInvestigatorPageState = (
  params: RouteParams
): SchemaPageState => {
  const query = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const getQueryValue = (key: string): string | undefined => {
    const value = query[key];

    return value ? String(value) : undefined;
  };

  const investigationId =
    params.investigationId ??
    getQueryValue(SCHEMA_INVESTIGATOR_INVESTIGATION_ID_KEY);

  const commonState = {
    // The /investigations/:id route carries no tableType in its path; an
    // investigation always targets an event schema.
    table_type: params.tableType ?? "event",
    smart_group_id: getQueryValue(SCHEMA_INVESTIGATOR_SMART_GROUP_ID_KEY),
  };

  // The URL only carries the filters the page was opened with; edits live in
  // React state and are mirrored to localStorage by the investigator page.
  // Prefer the mirror so Duck sees the current (possibly unsaved) filters and
  // the currently displayed investigation (sidebar selection doesn't navigate,
  // so the mirror's investigation id is more current than the URL's).
  const liveFilters = loadInvestigatorFiltersFromLocalStorage(
    investigationId,
    params.schemaName
  );

  if (liveFilters) {
    return {
      ...commonState,
      schema_name: params.schemaName ?? liveFilters.schemaName,
      investigation_id: liveFilters.displayedInvestigationId ?? undefined,
      // Unlike investigation_id, the URL wins here — ?smartGroupId= scopes the
      // page (the whole investigation list), mirroring the page's own
      // precedence. The mirror only fills in when the URL has none.
      smart_group_id:
        commonState.smart_group_id ?? liveFilters.smartGroupId ?? undefined,
      // "" (unlike the undefined of the URL fallback below) states the filters
      // are known to be empty rather than unknown to the client.
      events_filter: liveFilters.eventFilter ?? "",
      assets_filter: liveFilters.assetFilter ?? "",
    };
  }

  return {
    ...commonState,
    schema_name: params.schemaName,
    investigation_id: investigationId,
    events_filter: getQueryValue(SCHEMA_INVESTIGATOR_EVENT_FILTER_KEY),
    assets_filter: getQueryValue(SCHEMA_INVESTIGATOR_ASSET_FILTER_KEY),
  };
};

/**
 * Get information about the current state of the application
 * @param pages The pages config that has default values for some of the properties
 * @param isDuckFullscreen Whether the chat fills the page
 * @returns The current state of the application
 */
export const getAppState = (
  pages: PagesConfigModel,
  isDuckFullscreen: boolean
): AppState => {
  const { tab } = qs.parse(window.location.search, { ignoreQueryPrefix: true });

  const pathname = window.location.pathname;
  const match = getRouteMatch(pathname);
  const route = match?.key;
  const routeParams = match?.params ?? {};

  const commonState: AppState = {
    pathname,
    fullscreen_duck: isDuckFullscreen,
  };

  if (tab) {
    commonState.selected_tab = String(tab);
  }

  switch (route) {
    case RouteKey.ClaimAnalytics:
      return {
        ...commonState,
        claim_analytics: getClaimAnalyticsPageState(pages),
      };
    case RouteKey.SignalEventAnalytics:
      return {
        ...commonState,
        signal_event_analytics: getSignalEventAnalyticsPageState(pages),
      };
    case RouteKey.Investigator:
    case RouteKey.SmartGroup:
    case RouteKey.IssueInvestigator:
      return {
        ...commonState,
        investigator: getInvestigatorPageState(pages),
      };
    case RouteKey.Root:
      // Without a landing dashboard, `/` shows no page to describe.
      return pages.landingPage?.views?.length
        ? { ...commonState, dashboard: getDashboardPageState(pages) }
        : commonState;
    case RouteKey.SchemaEventAnalytics:
      return {
        ...commonState,
        dynamic_schema: getSchemaEventAnalyticsPageState(routeParams),
      };
    case RouteKey.SchemaRecords:
      return {
        ...commonState,
        dynamic_schema: getSchemaRecordsPageState(routeParams),
      };
    case RouteKey.SchemaRecord:
      return {
        ...commonState,
        dynamic_schema: getSchemaRecordPageState(routeParams),
      };
    case RouteKey.SchemaInvestigator:
    case RouteKey.SchemaInvestigatorById:
      return {
        ...commonState,
        dynamic_schema: getSchemaInvestigatorPageState(routeParams),
      };
    case RouteKey.SchemaDashboard:
      return {
        ...commonState,
        dynamic_schema: getSchemaDashboardPageState(),
      };
    default:
      return commonState;
  }
};
